import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChartBarIcon, ClockIcon, BeakerIcon, BoltIcon, AcademicCapIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import StandardNavbar from './StandardNavbar';
import { db } from '../firebase/config';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';

const CETScorePredictor = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [recent, setRecent] = useState({ mocks: [], practice: [] });
  const [coverage, setCoverage] = useState({ math: 0, physics: 0, chemistry: 0 });
  const [avgSecPerQ, setAvgSecPerQ] = useState(72);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        if (!user?.uid) { setLoading(false); return; }
        // Pull recent mock attempts (lightweight placeholder)
        const attemptsSnap = await getDocs(query(collection(db, 'mockAttemptResults'), where('userId', '==', user.uid)));
        const attempts = attemptsSnap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a,b)=> (b.completedAt?.toMillis?.() || 0) - (a.completedAt?.toMillis?.() || 0))
          .slice(0, 3);
        // Pull recent practice stats (placeholder — rely on userProgress later)
        const practice = []; // integrate when available
        setRecent({ mocks: attempts, practice });
        setLoading(false);
      } catch {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  // Heuristic calculations
  const estimate = useMemo(() => {
    // Subject accuracies placeholders from mocks if available
    // In v1, assume balanced split; refine when per-subject exists
    const mocks = recent.mocks || [];
    const practiceCount = 260; // placeholder until wired
    const daysWindow = 14;

    // Subject accuracies (placeholder 0.6 each if no data)
    const acc = { physics: 0.6, chemistry: 0.6, math: 0.62 };
    // Expected raw score
    const P = 50 * acc.physics;
    const C = 50 * acc.chemistry;
    const M = 100 * acc.math; // 2 marks/Q
    let score = P + C + M;
    // Pace factor
    const t = avgSecPerQ || 72;
    const alpha = 0.6;
    const f = Math.min(1, Math.pow(72 / t, alpha));
    score = score * f;
    // Percentile preview (no CDF for now)
    let percentile = null; // gate if no full mocks
    if (mocks.length >= 1) {
      percentile = Math.max(1, Math.min(99, Math.round((score / 200) * 100)));
    }
    // Confidence
    const conf = Math.max(0, Math.min(1,
      0.2 * Math.min(1, mocks.length / 3) +
      0.4 * Math.min(1, practiceCount / 300) +
      0.4 * 0.5 // coverage placeholder
    ));
    const band = Math.round((1 - conf) * 12);
    return {
      percentile, band, conf,
      P: Math.round(P), C: Math.round(C), M: Math.round(M),
      total: Math.round(score),
      basedOn: { mocks: mocks.length, recentQ: practiceCount, windowDays: daysWindow }
    };
  }, [recent, avgSecPerQ]);

  const chip = (label, action) => (
    <button onClick={action} className="px-3 py-1 rounded-full border text-sm hover:bg-gray-50">{label}</button>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <StandardNavbar title="MHT-CET (PCM) Score & Percentile Preview" showBack={true} showHome={true} />

      {/* Header card (live estimate) */}
      <section className="py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                  <ChartBarIcon className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <div className="text-sm text-gray-600">Predicted Percentile</div>
                  <div className="text-3xl font-bold text-gray-900">{estimate.percentile ?? '—'}{estimate.percentile===null?'':' '}<span className="text-base font-medium text-gray-600">{estimate.percentile!==null?`±${estimate.band}`:''}</span></div>
                  <div className="text-xs text-gray-500">Confidence: {estimate.conf<=0.33?'Low':estimate.conf<=0.66?'Medium':'High'}</div>
                </div>
              </div>
              <div className="text-sm text-gray-700">
                <div className="font-medium">Score breakdown</div>
                <div>Physics {estimate.P}/50 · Chemistry {estimate.C}/50 · Math {estimate.M}/100 ⇒ Total <span className="font-semibold">{estimate.total}/200</span></div>
                <div className="text-xs text-gray-500 mt-1">Based on: last {estimate.basedOn.mocks} mocks, {estimate.basedOn.recentQ} recent practice Q, past {estimate.basedOn.windowDays} days</div>
              </div>
              <div className="flex gap-2">
                <button onClick={()=>navigate('/diagnostic')} className="px-4 py-2 rounded-md bg-blue-600 text-white">Run 10-min Diagnostic</button>
                <button onClick={()=>navigate('/mock-tests')} className="px-4 py-2 rounded-md border">Take a Full Mock</button>
              </div>
            </div>
            <div className="mt-3 text-xs text-gray-600">Estimates get reliable after ≥3 recent mocks or a 10-min diagnostic.</div>
          </div>
        </div>
      </section>

      {/* Data completeness (trust meter) */}
      <section className="py-4">
        <div className="max-w-6xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow p-4 flex flex-wrap gap-2">
            {chip('Recency: Last mock 19 days ago', ()=>navigate('/mock-tests'))}
            {chip('Coverage: XI topics low in Chemistry', ()=>navigate('/practice'))}
            {chip('Volume: Only 110 recent Math Q', ()=>navigate('/practice'))}
          </div>
        </div>
      </section>

      {/* What moves your score (interactive) */}
      <section className="py-6">
        <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold text-gray-900 mb-4">What moves your score</h3>
            <div className="space-y-4 text-sm">
              <div>
                <label className="block text-gray-700 mb-1">Math accuracy ±5%</label>
                <input type="range" min="-5" max="5" defaultValue={0} className="w-full" onChange={(e)=>{}} />
              </div>
              <div>
                <label className="block text-gray-700 mb-1">Physics pace −10s/Q</label>
                <input type="range" min="-10" max="10" defaultValue={0} className="w-full" onChange={(e)=>{}} />
              </div>
              <div>
                <label className="block text-gray-700 mb-1">Chem coverage +2 topics</label>
                <input type="range" min="0" max="5" defaultValue={0} className="w-full" onChange={(e)=>{}} />
              </div>
              <div className="text-green-700">Live delta: +3.2 percentile</div>
              <div className="flex gap-2">
                <button onClick={()=>navigate('/practice')} className="px-3 py-2 rounded-md border">Fix Math weak area (10Q)</button>
                <button onClick={()=>navigate('/practice')} className="px-3 py-2 rounded-md border">Timed Physics set (10Q)</button>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Subject & pace breakdown</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between"><span>Physics</span><span>Accuracy 66% · Avg 68s/Q</span></div>
              <div className="flex items-center justify-between"><span>Chemistry</span><span>Accuracy 72% · Avg 70s/Q</span></div>
              <div className="flex items-center justify-between"><span>Math</span><span>Accuracy 64% · Avg 78s/Q</span></div>
              <div className="text-xs text-gray-500 mt-2">CET average pace ≈ 72s per question (180 min / 150 Q).</div>
            </div>
          </div>
        </div>
      </section>

      {/* College fit preview */}
      <section className="py-6">
        <div className="max-w-6xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold text-gray-900 mb-3">College fit preview</h3>
            <div className="flex flex-wrap gap-2 text-sm">
              <span className="px-3 py-1 rounded-full bg-green-50 text-green-700 border border-green-200">COEP Tech: Likely</span>
              <span className="px-3 py-1 rounded-full bg-yellow-50 text-yellow-700 border border-yellow-200">VJTI: Reach</span>
              <span className="px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">SPPU: Safety</span>
            </div>
            <button onClick={()=>navigate('/college-predictor')} className="mt-4 inline-flex items-center px-4 py-2 rounded-md bg-blue-600 text-white">
              Open College Predictor <ArrowRightIcon className="h-4 w-4 ml-2" />
            </button>
          </div>
        </div>
      </section>

      {/* Footnotes */}
      <section className="py-6">
        <div className="max-w-6xl mx-auto px-4 text-xs text-gray-600 space-y-1">
          <div>Percentile is a preview, not an official CET percentile.</div>
          <div>Error band depends on recency, volume, and coverage.</div>
          <div>Math: 2 marks/Q; Physics & Chemistry: 1 mark/Q; Total: 200.</div>
        </div>
      </section>
    </div>
  );
};

export default CETScorePredictor;
